/**
 * Admin Hooks
 * Reusable hooks for admin list, detail, and mutation operations
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import { adminGet, adminPost, adminPut, adminPatch, adminDelete, ADMIN_ENDPOINTS } from '../../lib/api/adminApi';

/**
 * Hook for admin list pages
 * @param {string} resource - Resource name (e.g., 'users', 'series')
 * @param {Object} initialParams - Initial query parameters
 * @returns {Object} List state and controls
 */
export const useAdminList = (resource, initialParams = {}) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthError, setIsAuthError] = useState(false);
  const [params, setParams] = useState({
    page: 1,
    limit: 25,
    ...initialParams,
  });
  
  const endpoint = ADMIN_ENDPOINTS[resource] || `/admin/${resource}`;
  const mountedRef = useRef(true);

  const fetchData = useCallback(async (overrideParams = {}) => {
    if (!mountedRef.current) return;
    
    setLoading(true);
    setError(null);
    setIsAuthError(false);
    
    const queryParams = { ...params, ...overrideParams };
    const result = await adminGet(endpoint, queryParams);
    
    if (!mountedRef.current) return;
    
    if (result.ok) {
      // Handle various response formats
      const items = result.data[resource] || result.data.items || result.data.data || [];
      const totalCount = result.data.total || result.data.count || items.length;
      
      setData(Array.isArray(items) ? items : []);
      setTotal(totalCount);
      setError(null);
    } else {
      setError(result.error);
      setIsAuthError(result.isAuthError);
      setData([]);
      setTotal(0);
    }
    
    setLoading(false);
  }, [endpoint, params, resource]);

  // Check auth and trigger initial fetch
  useEffect(() => {
    if (authLoading) return;
    
    if (!user || (user?.role !== 'admin' && !user?.is_admin)) {
      navigate('/');
      return;
    }
    
    // Use a flag to track if we should fetch
    let shouldFetch = true;
    
    const doFetch = async () => {
      if (!shouldFetch) return;
      await fetchData();
    };
    
    doFetch();
    
    return () => {
      shouldFetch = false;
    };
  }, [authLoading, user, navigate, fetchData]);

  // Cleanup ref on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const setPage = useCallback((page) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const setLimit = useCallback((limit) => {
    setParams(prev => ({ ...prev, limit, page: 1 }));
  }, []);

  const setFilter = useCallback((key, value) => {
    setParams(prev => ({ ...prev, [key]: value, page: 1 }));
  }, []);

  const setFilters = useCallback((filters) => {
    setParams(prev => ({ ...prev, ...filters, page: 1 }));
  }, []);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const totalPages = Math.ceil(total / params.limit);

  return {
    data,
    total,
    loading,
    error,
    isAuthError,
    params,
    page: params.page,
    limit: params.limit,
    totalPages,
    setPage,
    setLimit,
    setFilter,
    setFilters,
    setParams,
    refresh,
    // Helpers
    isEmpty: !loading && !error && data.length === 0,
    hasData: !loading && !error && data.length > 0,
    canPrevPage: params.page > 1,
    canNextPage: params.page < totalPages,
  };
};

/**
 * Hook for admin detail pages
 * @param {string} resource - Resource name (e.g., 'users', 'series')
 * @param {string} id - Resource ID
 * @returns {Object} Detail state and controls
 */
export const useAdminDetail = (resource, id) => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthError, setIsAuthError] = useState(false);
  
  const endpoint = ADMIN_ENDPOINTS[resource] || `/admin/${resource}`;
  const mountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    if (!mountedRef.current || !id) return;
    
    setLoading(true);
    setError(null);
    setIsAuthError(false);
    
    const result = await adminGet(`${endpoint}/${id}`);
    
    if (!mountedRef.current) return;
    
    if (result.ok) {
      setData(result.data);
      setError(null);
    } else {
      setError(result.error);
      setIsAuthError(result.isAuthError);
      setData(null);
    }
    
    setLoading(false);
  }, [endpoint, id]);

  // Check auth and trigger initial fetch
  useEffect(() => {
    if (authLoading) return;
    
    if (!user || (user?.role !== 'admin' && !user?.is_admin)) {
      navigate('/');
      return;
    }
    
    let shouldFetch = true;
    
    const doFetch = async () => {
      if (!shouldFetch) return;
      await fetchData();
    };
    
    doFetch();
    
    return () => {
      shouldFetch = false;
    };
  }, [authLoading, user, navigate, fetchData]);

  // Cleanup ref on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const updateLocal = useCallback((updates) => {
    setData(prev => prev ? { ...prev, ...updates } : prev);
  }, []);

  return {
    data,
    loading,
    error,
    isAuthError,
    refresh,
    updateLocal,
    // Helpers
    hasData: !loading && !error && data !== null,
    notFound: !loading && error && !isAuthError,
  };
};

/**
 * Hook for admin mutations (create, update, delete)
 * @param {string} resource - Resource name
 * @returns {Object} Mutation functions and state
 */
export const useAdminMutation = (resource) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthError, setIsAuthError] = useState(false);
  
  const endpoint = ADMIN_ENDPOINTS[resource] || `/admin/${resource}`;

  const execute = useCallback(async (method, idOrData, data = null) => {
    setLoading(true);
    setError(null);
    setIsAuthError(false);
    
    let result;
    
    switch (method) {
      case 'create':
        result = await adminPost(endpoint, idOrData);
        break;
      case 'update':
        result = await adminPut(`${endpoint}/${idOrData}`, data);
        break;
      case 'patch':
        result = await adminPatch(`${endpoint}/${idOrData}`, data);
        break;
      case 'delete':
        result = await adminDelete(`${endpoint}/${idOrData}`);
        break;
      default:
        result = { ok: false, error: 'Invalid mutation method' };
    }
    
    setLoading(false);
    
    if (!result.ok) {
      setError(result.error);
      setIsAuthError(result.isAuthError);
    }
    
    return result;
  }, [endpoint]);

  const create = useCallback((data) => execute('create', data), [execute]);
  const update = useCallback((id, data) => execute('update', id, data), [execute]);
  const patch = useCallback((id, data) => execute('patch', id, data), [execute]);
  const remove = useCallback((id) => execute('delete', id), [execute]);

  const reset = useCallback(() => {
    setError(null);
    setIsAuthError(false);
  }, []);

  return {
    loading,
    error,
    isAuthError,
    create,
    update,
    patch,
    remove,
    reset,
  };
};

/**
 * Hook for admin action (single POST action like ban, approve, etc.)
 */
export const useAdminAction = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (endpoint, data = {}) => {
    setLoading(true);
    setError(null);
    
    const result = await adminPost(endpoint, data);
    
    setLoading(false);
    
    if (!result.ok) {
      setError(result.error);
    }
    
    return result;
  }, []);

  return { execute, loading, error };
};

export default {
  useAdminList,
  useAdminDetail,
  useAdminMutation,
  useAdminAction,
};
